import React from 'react'
import Input from 'components/input'
import Checkbox from 'components/checkbox'
import Field from 'components/field'
import { TEXT_COLUMN_TYPES, SELECT_COLUMN_TYPES } from 'components/spreadsheet/constants/const'
import { IColumnTypes } from 'components/spreadsheet/types'

// The validation settings shared by the column configuration UIs. Field names/types mirror the column
// model so a consumer can pass its draft/column straight through as `value` and route `onChange` patches
// to wherever it persists them.
export interface ColumnValidationValue {
  stringValidation: string | null
  validationMessage: string | null
  validationNoDuplicates: boolean
  validationNoBlanks: boolean
  hardValidation: boolean
  blockInvalidSelectValues: boolean
  displayValidationError: boolean
}

interface ColumnValidationFieldsProps {
  kind: IColumnTypes
  value: ColumnValidationValue
  onChange: (patch: Partial<ColumnValidationValue>) => void
}

// The validation section body (regex, message, and the allow/show toggles) used wherever a column's
// validation is configured — the add-column configure stage and the Edit Column menu. The checkbox labels
// read positively ("Allow …") while the stored flags are the negatives (validationNoDuplicates etc.), so
// each checkbox inverts its bound value.
const ColumnValidationFields: React.FC<ColumnValidationFieldsProps> = ({ kind, value, onChange }) => {
  const isText = TEXT_COLUMN_TYPES.includes(kind)
  const isSelect = SELECT_COLUMN_TYPES.includes(kind)

  return (
    <>
      {isText && (
        <Field label="Text Validation (Regex)" htmlFor="column-regex">
          <Input
            id="column-regex"
            type="text"
            value={value.stringValidation ?? ''}
            onChange={(event) => onChange({ stringValidation: event.target.value || null })}
          />
        </Field>
      )}
      <Field label="Message To Display on Invalid Data Entry (optional)" htmlFor="column-validation-message">
        <Input
          id="column-validation-message"
          type="text"
          value={value.validationMessage ?? ''}
          onChange={(event) => onChange({ validationMessage: event.target.value || null })}
        />
      </Field>
      <div className="px-2.5">
        <Checkbox
          checked={!value.validationNoDuplicates}
          onChange={(event) => onChange({ validationNoDuplicates: !event.target.checked })}
        >
          Allow Duplicate Values
        </Checkbox>
        <Checkbox
          checked={!value.validationNoBlanks}
          onChange={(event) => onChange({ validationNoBlanks: !event.target.checked })}
        >
          Allow Blank Values
        </Checkbox>
        <Checkbox
          checked={!value.hardValidation}
          onChange={(event) => onChange({ hardValidation: !event.target.checked })}
        >
          Allow Invalid Data Entry (Regex, Blanks, Duplicates)
        </Checkbox>
        {isSelect && (
          <Checkbox
            checked={!value.blockInvalidSelectValues}
            onChange={(event) => onChange({ blockInvalidSelectValues: !event.target.checked })}
          >
            Allow Invalid Select Data Entry
          </Checkbox>
        )}
        <Checkbox
          checked={value.displayValidationError}
          onChange={(event) => onChange({ displayValidationError: event.target.checked })}
        >
          Show Validation Error Symbol
        </Checkbox>
      </div>
    </>
  )
}

export default ColumnValidationFields
